import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { Form, FormGroup, Placeholder } from 'semantic-ui-react';
import { RateMetadataHook } from '../../../../hooks/rate';
import {
  billingCycleOptions,
  BillingService,
  CommonUtility,
  ToastMessage,
} from '../../../../utility';
import { prepareRateCategories, RateCardFields } from '../../../../page-components';
import { AlertFooterBar } from '../../../../elements/FooterAlert';
import {
  CheckboxFormField,
  ControlledTextAreaFormField,
  ControlledTextFormField,
  DropdownFormField,
  ShadowBox,
} from '../../../../elements';
import { useGlobalContext } from '../../../../contexts';

const WarehouseSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  warehouseId: yup.string().required('Warehouse is required'),
  description: yup.string().optional(),
  isActive: yup.boolean().nullable(),
  billingCycleDays: yup
    .array()
    .min(1, 'Billing Cycle is required')
    .required('Billing Cycle is required'),
});

export function CreateRate() {
  const [userValue, setUserValue] = useState({});
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  const { warehouseOptions, warehouseLoading } = useGlobalContext();

  const {
    formState: { errors, dirtyFields },
    control,
    reset,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(WarehouseSchema),
  });

  const [changed, setChanged] = useState(false);

  const { data: metadata, loading } = RateMetadataHook();

  const onValueChange = (categoryName, serviceName, keyName, value) => {
    setChanged(true);
    setUserValue(userValue => ({
      ...userValue,
      [categoryName]: {
        ...userValue[categoryName],
        [serviceName]: {
          ...userValue[categoryName][serviceName],
          [keyName]: value,
        },
      },
    }));
  };

  const populateUserValue = () => {
    const value = {};
    metadata.categories.forEach(category => {
      category.services.forEach(service => {
        const emptyService = {
          rate: '0',
          billingUnit: service?.billingOptions?.[0] || '',
          name: service.name,
          enabled: false,
        };

        if (service.uniqueName) {
          emptyService.uniqueName = service.uniqueName;
        }

        if (value[category.name]) {
          value[category.name] = {
            ...value[category.name],
            [service.uniqueName || service.name]: emptyService,
          };
        } else {
          value[category.name] = {
            [service.uniqueName || service.name]: emptyService,
          };
        }
      });
    });
    setUserValue(value);
  };

  useEffect(() => {
    if (CommonUtility.isValidObject(metadata)) {
      resetFields();
    }
  }, [metadata]);

  const resetFields = () => {
    reset({
      name: '',
      description: '',
      isActive: true,
      billingCycleDays: [1],
    });
    populateUserValue();
    setChanged(false);
  };

  const create = async formValues => {
    try {
      setProcessing(true);
      const payload = {
        name: formValues.name,
        description: formValues.description,
        isActive: formValues.isActive,
        warehouseId: formValues?.warehouseId,
        billingCycleDays: formValues.billingCycleDays,
        categories: prepareRateCategories(userValue, metadata),
      };

      const res = await BillingService.add(payload);
      ToastMessage.success('Service created successfully');
      navigate(`/app/settings/services-rates/${res._id}`);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  if (processing || loading) {
    return (
      <div className="mt-3">
        <ShadowBox>
          <Placeholder fluid>
            <Placeholder.Header>
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Line />
            </Placeholder.Header>
          </Placeholder>
        </ShadowBox>
        <ShadowBox className="mt-4">
          <Placeholder fluid>
            <Placeholder.Header>
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Line />
            </Placeholder.Header>
            <Placeholder.Header>
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Line />
            </Placeholder.Header>
          </Placeholder>
        </ShadowBox>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <ShadowBox>
        <h3>Information</h3>
        <Form>
          <FormGroup widths="equal">
            <ControlledTextFormField
              control={control}
              name="name"
              errors={errors.name}
              hint={errors?.name?.message}
              label="Name"
            />
            <DropdownFormField
              control={control}
              name="warehouseId"
              errors={errors.warehouseId}
              hint={errors?.warehouseId?.message}
              label="Warehouse"
              options={warehouseOptions}
              loading={warehouseLoading}
            />
            <ControlledTextAreaFormField
              control={control}
              name="description"
              errors={errors.description}
              hint={errors?.description?.message}
              label="Description"
              rows={1}
            />
            <CheckboxFormField
              control={control}
              name="isActive"
              errors={errors.isActive}
              hint={errors?.isActive?.message}
              label="Active"
            />
          </FormGroup>
          <b>Scheduling</b>
          <FormGroup className="mt-4 mb-0">
            <DropdownFormField
              control={control}
              name="billingCycleDays"
              errors={errors.billingCycleDays}
              hint={errors?.billingCycleDays?.message}
              label="Billing Cycle Day"
              options={billingCycleOptions}
              multiple
              width={4}
              sorted={false}
            />
          </FormGroup>
        </Form>
      </ShadowBox>
      <RateCardFields
        metadata={metadata}
        userValue={userValue}
        onValueChange={onValueChange}
      />
      {(CommonUtility.isValidObject(dirtyFields) || changed) && (
        <AlertFooterBar
          message="Save Changes"
          cancel={resetFields}
          save={handleSubmit(create)}
        />
      )}
    </div>
  );
}
